import React from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, ShieldCheck, Clock3, Zap, BadgeIndianRupee, ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONES, WHATSAPP_URL, TRUST_BADGES } from "@/constants/content";
import { useBooking } from "@/contexts/BookingContext";

const badgeIcon = { ShieldCheck, Clock3, Zap, BadgeIndianRupee };

export default function Hero() {
  const { open } = useBooking();
  return (
    <section
      id="home"
      className="relative pt-24 md:pt-28 pb-16 md:pb-24 overflow-hidden bg-hero-gradient"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-medical-grid opacity-70 pointer-events-none" />
      <div className="container-page grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-slate-700 text-xs font-medium mb-6">
            <span className="flex -space-x-1.5">
              {[1, 2, 3].map((i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              ))}
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            </span>
            <span className="font-semibold">4.9/5</span>
            <span className="text-slate-400">·</span>
            <span>Trusted by 2,500+ families in Delhi NCR</span>
          </div>

          <h1 className="font-display font-bold text-slate-900 text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight hero-text-balance">
            24×7 Home Healthcare Services in{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">Delhi NCR</span>
              <span className="absolute -bottom-1 left-0 right-0 h-2.5 bg-gradient-to-r from-[#0A84FF]/20 to-[#10B981]/20 rounded-full blur-sm" />
            </span>
          </h1>

          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
            Professional nurses, ICU setup, physiotherapy, elder care and critical care services — delivered at your home with warmth, precision, and 24×7 availability.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={() => open()}
              className="rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-7 h-12 text-sm font-semibold shadow-lg shadow-[#0A84FF]/25 hover:-translate-y-0.5 transition-all"
              data-testid="hero-book-btn"
            >
              Book Appointment <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <a
              href={`tel:${PHONES[0]}`}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-900 px-6 h-12 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
              data-testid="hero-call-btn"
            >
              <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call Now
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white px-6 h-12 text-sm font-semibold shadow-lg shadow-[#25D366]/25"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Now
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            {TRUST_BADGES.map((b) => {
              const Icon = badgeIcon[b.icon] || ShieldCheck;
              return (
                <div key={b.label} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/80 backdrop-blur border border-slate-200/80" data-testid={`hero-badge-${b.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#0A84FF]/15 to-[#10B981]/15 flex items-center justify-center">
                    <Icon className="h-3.5 w-3.5" style={{ color: '#0A84FF' }} />
                  </div>
                  <span className="text-[12px] font-semibold text-slate-700 leading-tight">{b.label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-4 md:p-5 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-bold mb-3" style={{ color: '#EF4444' }}>
              <Phone className="h-3 w-3" /> 24×7 Emergency Numbers
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {PHONES.map((p) => (
                <a key={p} href={`tel:${p}`} className="font-display font-bold text-slate-900 text-base md:text-lg hover:text-[#0A84FF] transition-colors" data-testid={`hero-phone-${p}`}>
                  {p}
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative"
        >
          <div className="relative rounded-[36px] overflow-hidden ring-1 ring-slate-200/70 premium-shadow">
            <img
              src="https://images.unsplash.com/photo-1758691462477-976f771224d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxudXJzZSUyMGhlbHBpbmclMjBlbGRlcmx5JTIwcGF0aWVudCUyMGhvbWV8ZW58MHx8fHwxNzc4MTMzOTMzfDA&ixlib=rb-4.1.0&q=85"
              alt="Professional nurse helping elderly patient at home"
              className="w-full h-[480px] md:h-[580px] object-cover"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="absolute -bottom-5 left-4 md:-left-8 glass rounded-2xl premium-shadow p-4 flex items-center gap-3 max-w-[260px] float-slow"
          >
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0A84FF] to-[#10B981] flex items-center justify-center text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] text-slate-500 font-medium">Verified Nurses</div>
              <div className="font-display font-bold text-slate-900">100% Background Checked</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="absolute top-6 -right-3 md:-right-6 glass rounded-2xl premium-shadow p-4 flex items-center gap-3"
          >
            <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white pulse-ring" style={{ background: '#EF4444' }}>
              <Clock3 className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] text-slate-500 font-medium">Same-Day Response</div>
              <div className="font-display font-bold text-slate-900">Within 2 Hours</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="hidden md:flex absolute top-1/2 -left-8 glass rounded-2xl premium-shadow p-3 items-center gap-2"
          >
            <div className="flex -space-x-2">
              {["https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=100",
                "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=100",
                "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=100"].map((u, i) => (
                <img key={i} src={u} alt="patient family" className="h-8 w-8 rounded-full ring-2 ring-white object-cover" />
              ))}
            </div>
            <div className="text-[11px]">
              <div className="font-bold text-slate-900">2,500+</div>
              <div className="text-slate-500">families served</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
